import { useEffect, useReducer } from 'react'
import { useIntl } from 'react-intl'
import { LoginFlowState, type LoginFlow, type Session } from '@ory/client'

import { logClientError, translateClientError } from '@i18n/kratos/kratos-client-error'
import { assertNever } from '@utils/assert.utils'
import { fetchLoginFlow, fetchWhoami } from '@utils/kratos.utils'
import {
  buildRecoveryUrl,
  consumeStoredChallenge,
  extractLoginChallenge,
  KRATOS_LOGIN_FALLBACK,
  logRecovery,
  markRecoveryAttempted,
  type RecoveryFrom,
  wasRecoveryAttempted,
  writeStoredChallenge,
} from '@utils/oauth-challenge-stash.utils'

export type LoginViewState =
  | { kind: 'loading' }
  | { kind: 'session'; session: Session }
  | { kind: 'flow'; flow: LoginFlow }
  | { kind: 'error'; message: string }

type Action =
  | { type: 'session'; session: Session }
  | { type: 'flow'; flow: LoginFlow }
  | { type: 'error'; message: string }

const reducer = (_state: LoginViewState, action: Action): LoginViewState => {
  switch (action.type) {
    case 'session':
      return { kind: 'session', session: action.session }
    case 'flow':
      return { kind: 'flow', flow: action.flow }
    case 'error':
      return { kind: 'error', message: action.message }
    default:
      return assertNever(action)
  }
}

// `from` is a diagnostic tag only — recovery target is the same for every caller.
const redirectWithRecovery = (from: Exclude<RecoveryFrom, 'authenticated'>): void => {
  const challenge = consumeStoredChallenge()
  if (challenge) {
    logRecovery(from)
  }
  window.location.replace(challenge ? buildRecoveryUrl(challenge) : KRATOS_LOGIN_FALLBACK)
}

export const useLoginFlow = (flowId: string | null): LoginViewState => {
  const intl = useIntl()
  const [state, dispatch] = useReducer(reducer, { kind: 'loading' })

  useEffect(() => {
    if (flowId) {
      const controller = new AbortController()
      fetchLoginFlow(flowId, controller.signal).then((result) => {
        if (controller.signal.aborted) {
          return
        }
        switch (result.kind) {
          case 'ok': {
            // Stash so a later mount on bare /login can replay the challenge.
            const challenge = extractLoginChallenge(result.flow.return_to)
            if (challenge) {
              writeStoredChallenge(challenge)
            }
            // Kratos serves consumed flows as 200 + state=passed_challenge.
            // Recover once per challenge; no challenge = no fuse, just show
            // the form so we can't loop.
            const recoverableChallenge =
              result.flow.state === LoginFlowState.PassedChallenge &&
              challenge !== null &&
              !wasRecoveryAttempted(challenge)
                ? challenge
                : null
            if (recoverableChallenge) {
              markRecoveryAttempted(recoverableChallenge)
              redirectWithRecovery('passed_challenge')
              return
            }
            dispatch({ type: 'flow', flow: result.flow })
            break
          }
          case 'expired':
            redirectWithRecovery('expired')
            break
          case 'error':
            logClientError(result.code, result.message)
            dispatch({ type: 'error', message: translateClientError(intl, result.code) })
            break
          default:
            assertNever(result)
        }
      })
      return () => controller.abort()
    }

    // Without whoami first, an active session 303s into default_browser_return_url
    // which bounces back here → loop.
    const controller = new AbortController()

    fetchWhoami(controller.signal).then((result) => {
      if (controller.signal.aborted) {
        return
      }

      switch (result.kind) {
        case 'authenticated': {
          // Active session + stash = OAuth chain lost its return_to; replay it.
          const challenge = consumeStoredChallenge()
          if (challenge) {
            logRecovery('authenticated')
            window.location.replace(buildRecoveryUrl(challenge))
            return
          }
          dispatch({ type: 'session', session: result.session })
          break
        }
        case 'anonymous':
          redirectWithRecovery('anonymous')
          break
        case 'error':
          logClientError(result.code, result.message)
          dispatch({ type: 'error', message: translateClientError(intl, result.code) })
          break
        default:
          assertNever(result)
      }
    })

    return () => controller.abort()

    // eslint-disable-next-line react-hooks/exhaustive-deps -- intl is a fresh IntlShape on every LanguageSwitcher click
  }, [flowId])

  return state
}
